import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCancel, faCopy,  faPlus, faSave, faSquareXmark, faTrashCan } from "@fortawesome/free-solid-svg-icons"
import {
  Box,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useFetchWithBody, usePost } from "../../api/reactQueryCustomHooks";
import { apiRoute } from "../../constants/enum";
import { useSelector } from "react-redux";
import { COLUMN_ALIGN_TYPE } from "../../components/NextUITable";
import { ErrorCard, LoadingCard, MainContainer } from "../../components";
import { Button, Card, CardBody, Input } from "@heroui/react";

const LWMaster = () => {
  const [selectedShape, setSelectedShape] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [rows, setRows] = useState([]);
  const [copyMode, setCopyMode] = useState(false);
  const [targetShape, setTargetShape] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingCopyData, setPendingCopyData] = useState([]);

  const { user } = useSelector((state) => state.auth);

  const { data: shapes = [], isLoading, isError } = useFetchWithBody({
    url: apiRoute.lwMaster.getshape,
    queryKey: "shapes",
    inputData: {},
  });

  const {
    data: lwData = [],
    isLoading: isLwLoading,
    isError: isLwError,
  } = useFetchWithBody({
    url: apiRoute.lwMaster.getLWMaster,
    queryKey: ["lwData", selectedShape?.S_Code],
    inputData: { s_code: selectedShape?.S_Code },
    enabled: !!selectedShape,
  });

  const updateLWMaster = usePost({
    url: apiRoute.lwMaster.updateLWMaster,
    queryKey: "updateData",
  });

  const deleteLWMaster = usePost({
    url: apiRoute.lwMaster.deleteLWMaster,
    queryKey: "DeleteLWMASTER",
  });

  const insertLWMaster = usePost({
    url: apiRoute.lwMaster.insertLWMaster,
    queryKey: "InsertLWmaster",
  });

  const handleCopyClick = () => {
    setCopyMode(true);
    setTargetShape(null);
  };

  const handleTargetShapeSelect = (targetCode) => {
    const target = shapes.find((s) => s.S_Code === targetCode);
    setTargetShape(target);

    if (target && selectedShape && lwData.length) {
      const copiedPayload = lwData.map((item) => ({
        s_code: target.S_Code,
        lwcode: item.lwcode,
        f_dia: item.f_dia,
        t_dia: item.t_dia,
        f_lnth: item.f_lnth,
        t_lnth: item.t_lnth,
        lw_ord: item.lw_ord,
      }));

      setPendingCopyData(copiedPayload);
      setConfirmOpen(true);
    }
  };

  const handleConfirmInsert = () => {
    insertLWMaster.mutate({
      data: {
        db_user: user.username,
        data: pendingCopyData,
      },
    });
    setConfirmOpen(false);
    setCopyMode(false);
    setPendingCopyData([]);
  };

  const handleCancelInsert = () => {
    setConfirmOpen(false);
    setPendingCopyData([]);
  };

  useEffect(() => {
    if (lwData && lwData.length) {
      const dataWithId = lwData
        .sort((a, b) => Number(a.lw_ord) - Number(b.lw_ord))
        .map((item, index) => ({ id: index + 1, ...item }));
      setRows(dataWithId);
    }
  }, [lwData]);

  const handleSelectShape = (code) => {
    const shape = shapes.find((s) => s.S_Code === code);
    setSelectedShape(shape);
    setRows([]);
  };

  const handleAddRow = () => {
    const newRow = {
      id: rows.length ? Math.max(...rows.map((r) => r.id)) + 1 : 1,
      lwcode: "",
      f_dia: "",
      t_dia: "",
      f_lnth: "",
      t_lnth: "",
      lw_ord: "",
    };
    setRows((prev) => [...prev, newRow]);
  };

  const handleDelete = (id) => {
    const deletedRow = rows.find((r) => r.id === id);
    setRows((prev) => prev.filter((row) => row.id !== id));

    if (selectedShape && deletedRow?.lwcode) {
      deleteLWMaster.mutate({
        data: {
          s_code: selectedShape?.S_Code,
          lwcode: deletedRow.lwcode,
        },
      });
    }
  };

  const handleRowUpdate = (newRow, oldRow) => {
    setRows((prev) => prev.map((r) => (r.id === newRow.id ? newRow : r)));

    if (!selectedShape || !newRow.lwcode?.trim()) return newRow;

    const order = Number(newRow.lw_ord);
    if (
      !newRow.f_dia ||
      !newRow.t_dia ||
      !newRow.f_lnth ||
      !newRow.t_lnth ||
      isNaN(order) ||
      order === 0
    ) {
      return newRow;
    }

    const isExisting = lwData.some((r) => r.lwcode === newRow.lwcode);

    const payload = {
      s_code: selectedShape.S_Code,
      lwcode: newRow.lwcode,
      f_dia: newRow.f_dia,
      t_dia: newRow.t_dia,
      f_lnth: newRow.f_lnth,
      t_lnth: newRow.t_lnth,
      lw_ord: order,
    };

    if (isExisting) {
      updateLWMaster.mutate({ data: payload });
    } else {
      insertLWMaster.mutate({
        data: {
          db_user: user.username,
          data: [payload],
        },
      });
    }

    return newRow;
  };

  const filteredShapes = shapes.filter((shape) =>
    shape.S_Name.toLowerCase().includes(searchTerm.toLowerCase())
  );


  const itemGridHeadClass = "bill-grid-style"
  const columns = [
    {
      field: "actions",
      headerName: "Delete",
      headerAlign: "center",
      minWidth: 80,
      headerClassName: itemGridHeadClass,
      align: "center",
      flex: 1,
      renderCell: (params) => (
        <FontAwesomeIcon
          color="red"
          className={`cursor-pointer`}
          onClick={() => handleDelete(params.id)}
          icon={faTrashCan}
        ></FontAwesomeIcon>

      ),
    },
    {
      field: "lwcode",
      headerAlign: "center",
      align: "center",
      headerName: "LW Code",
      minWidth: 130,
      flex: 1, editable: true, headerClassName: itemGridHeadClass,
    },
    { field: "f_lnth", headerAlign: "center", headerClassName: itemGridHeadClass, align: "center", headerName: "From Length", minWidth: 100, flex: 1, editable: true },
    { field: "t_lnth", headerAlign: "center", headerClassName: itemGridHeadClass, align: "center", headerName: "To Length", minWidth: 100, flex: 1, editable: true,  },
    { field: "f_dia", headerAlign: "center", align: "center", headerClassName: itemGridHeadClass, headerName: "From Diam", minWidth: 100, flex: 1, editable: true, },
    { field: "t_dia", headerAlign: "center", align: "center", headerClassName: itemGridHeadClass, headerName: "To Diam", minWidth: 100, flex: 1, editable: true, },
    
    {
      field: "lw_ord",
      headerName: "Order",
      headerAlign: "center",
      align: "center",
      minWidth: 80,
      editable: true,
      flex: 1,
      headerClassName: itemGridHeadClass,
      //type: "number",
    },

  ];

  if (isLoading) return <LoadingCard />;
  if (isError) return <ErrorCard />;

  return (
    <MainContainer
      pageName="LW Entry"
      breadcrumbs={["Diamond Parameter", "LW Master"]}

    >

      <Card shadow="sm" className="mb-3 w-full shadow p-3 bg-white">
        <CardBody>
          <div className="flex flex-col md:flex-row gap-4 ">
            <div className="md:w-1/4 w-full">
              <h2 className="text-xl font-semibold  text-blue-700 mb-3 text-center">Select Shape</h2>
              <Input

                label="Search Shape"
                variant="bordered"
                size="sm"
                className="mb-3"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <hr />
              <br />
              <TextField
                fullWidth
                select
                label="Select Shape"
                variant="outlined"
                size="small"
                classes={"text-xs"}
                value={selectedShape?.S_Code || ""}
                onChange={(e) => handleSelectShape(e.target.value)}
              >
                {filteredShapes.map((shape) => (
                  <MenuItem key={shape.S_Code} value={shape.S_Code}>
                    {shape.S_Name}
                  </MenuItem>
                ))}
              </TextField>

              <hr />
              <br />
              <Button variant="shadow" className="w-full"
                startContent={<FontAwesomeIcon icon={faCopy} ></FontAwesomeIcon>}
                size="sm" color="danger" onPress={handleCopyClick}>
                COPY TO ANOTHER SHAPE
              </Button>

              {copyMode && (
                <div className="my-5">
                  <TextField
                    fullWidth
                    select
                    label="Select Target Shape"
                    variant="outlined"
                    size="small"
                    value={targetShape?.S_Code || ""}
                    onChange={(e) => handleTargetShapeSelect(e.target.value)}
                  >
                    {shapes
                      .filter((shape) => shape.S_Code !== selectedShape?.S_Code)
                      .map((shape) => (
                        <MenuItem key={shape.S_Code} value={shape.S_Code}>
                          {shape.S_Name}
                        </MenuItem>
                      ))}
                  </TextField>
                </div>
              )}
            </div>

            <div className="md:w-3/4 w-full">
              <h2 className="text-xl font-semibold mb-4 text-blue-700 text-center">Shape LW Details</h2>
              {selectedShape ? (
                isLwLoading ? (
                  <p className="text-center text-gray-600">Loading data...</p>
                ) : isLwError ? (
                  <p className="text-center text-red-500">Error loading data.</p>
                ) : (
                  <div className="p-4 bg-gray-100 rounded-lg shadow">
                    <div className="flex justify-between mb-2">
                      <Button variant="shadow" size="sm" color="primary"
                        startContent={<FontAwesomeIcon icon={faPlus}></FontAwesomeIcon>} onPress={handleAddRow}>
                        ADD ROW
                      </Button>
                    </div>
                    <Box sx={{ background:"#ffffff", height: rows.length > 10 ? 400 : "auto", width: '100%' }}>
                      <DataGrid
                        rows={rows}
                        columns={columns}
                        processRowUpdate={handleRowUpdate}
                        experimentalFeatures={{ newEditingApi: true }}
                        rowHeight={30}
                        head
                        hideFooter={true}
                        columnHeaderHeight={35}

                        checkboxSelection
                        disableRowSelectionOnClick
                        getRowClassName={(params) => {

                          return 'row-class';
                        }}

                        // getRowClassName={getRowClassName}
                        sx={{
                          '& .MuiDataGrid-columnHeaders': {
                            position: 'sticky',
                            top: 0,
                            zIndex: 1, // Make sure the headers are above the rows when scrolling

                          },
                          '& .row-class': {
                            fontSize: "12px",
                            textAlign: "center"
                          },

                        }}
                      />
                    </Box>
                  </div>
                )
              ) : (
                <p className="text-center text-gray-500">Please select a shape to view details.</p>
              )}
            </div>

            {/* Confirmation Dialog */}
            
            <Dialog open={confirmOpen} className="border-1" onClose={handleCancelInsert}>
              
              <DialogContent>

                <Typography>
                  <div className="text-sm text-gray-700" >
                  Are you sure you want to copy data from{" "}
                  <strong>{selectedShape?.S_Name}</strong> to{" "}
                  <strong>{targetShape?.S_Name}</strong>?
                  </div>
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button variant="flat" onPress={handleCancelInsert} color="danger">
                  Cancel
                </Button>
                <Button variant="flat" onPress={handleConfirmInsert}  color="success">
                  Yes, Copy
                </Button>
              </DialogActions>
            </Dialog>
          </div>
        </CardBody>
      </Card>
    </MainContainer>
  );
};

export default LWMaster;
